export const API_URL = (import.meta.env.VITE_API_URL as string | undefined) ?? "http://localhost:8787";

export class ApiError extends Error {
  status: number;
  constructor(message: string, status: number) {
    super(message);
    this.status = status;
  }
}

export interface IdentityHeaders {
  initDataRaw: string | null;
  anonymousId: string | null;
}

let identity: IdentityHeaders = { initDataRaw: null, anonymousId: null };
export function setApiIdentity(next: IdentityHeaders): void {
  identity = next;
}

// No essential startup/quiz request may hang indefinitely on a flaky mobile
// connection — that's the "have to close and reopen the Mini App" failure
// mode. Every request is bounded by this timeout and turned into a normal,
// recoverable ApiError instead of hanging forever.
const REQUEST_TIMEOUT_MS = 10_000;

async function attempt<T>(path: string, init: RequestInit, headers: Record<string, string>): Promise<T> {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS);

  let res: Response;
  try {
    res = await fetch(`${API_URL}${path}`, { ...init, headers, credentials: "include", signal: controller.signal });
  } catch (error) {
    if (error instanceof DOMException && error.name === "AbortError") {
      throw new ApiError("Сервер не отвечает. Проверьте подключение и повторите попытку.", 0);
    }
    throw new ApiError("Сеть недоступна. Проверьте подключение и повторите попытку.", 0);
  } finally {
    clearTimeout(timer);
  }

  if (!res.ok) {
    let message = `Ошибка запроса (${res.status})`;
    try {
      const body = (await res.json()) as { error?: string };
      if (body.error) message = body.error;
    } catch {
      // ignore — use default message
    }
    throw new ApiError(message, res.status);
  }

  return res.json() as Promise<T>;
}

/**
 * GET requests are safe to retry transparently — they never mutate state, so
 * one bounded retry on a transient network failure (dropped connection,
 * timeout) recovers silently instead of surfacing an error for something a
 * moment's flakiness would have fixed. `status: 0` on an ApiError means the
 * request never got a response at all (network/timeout, not a real HTTP
 * error), which is exactly the transient case worth retrying — an actual
 * HTTP error response (4xx/5xx) is not retried here.
 *
 * POST/PUT/PATCH/DELETE are never auto-retried: a request that mutates state
 * (creating a session, submitting an answer) may have already succeeded
 * server-side even if the client never saw the response, so a silent retry
 * risks a duplicate write. Those surface as a normal recoverable error
 * instead — the existing UI (disabled-until-loading buttons, visible error
 * text) already lets the user retry deliberately.
 */
async function request<T>(path: string, init: RequestInit = {}, retryOnNetworkError = false): Promise<T> {
  const headers: Record<string, string> = {
    "content-type": "application/json",
    ...(init.headers as Record<string, string> | undefined),
  };
  if (identity.initDataRaw) headers["X-Telegram-Init-Data"] = identity.initDataRaw;
  if (identity.anonymousId) headers["X-Anonymous-Id"] = identity.anonymousId;

  try {
    return await attempt<T>(path, init, headers);
  } catch (error) {
    if (retryOnNetworkError && error instanceof ApiError && error.status === 0) {
      return attempt<T>(path, init, headers);
    }
    throw error;
  }
}

export const api = {
  get: <T>(path: string) => request<T>(path, undefined, true),
  post: <T>(path: string, body?: unknown) =>
    request<T>(path, { method: "POST", body: body ? JSON.stringify(body) : undefined }),
  put: <T>(path: string, body?: unknown) =>
    request<T>(path, { method: "PUT", body: body ? JSON.stringify(body) : undefined }),
  patch: <T>(path: string, body?: unknown) =>
    request<T>(path, { method: "PATCH", body: body ? JSON.stringify(body) : undefined }),
  delete: <T>(path: string) => request<T>(path, { method: "DELETE" }),
};
